import type { ASMTreeNode, RawAssemblyTree } from '@/state'
import type { Step } from '@/services/queries/operation_steps/types'

export const isAssemblyNodeLocked = ({
  node,
  tree,
  steps,
  subAssemblyDocumentPageId,
}: {
  node?: ASMTreeNode
  tree?: RawAssemblyTree
  steps: Step[]
  subAssemblyDocumentPageId?: string | null
}): boolean => {
  if (!tree || !node) {
    return true
  }

  // Always lock the root node
  const root = tree.nodes.find((n) => n.uuid === tree.root)
  if (!root) {
    return true
  }

  const relevantSteps = steps.filter(
    (step) => step.parent === subAssemblyDocumentPageId,
  )
  const assemblyGroupIdsInOperationStep = relevantSteps.flatMap((step) =>
    step.assembly_group_ids.filter((id) => id !== root?.uuid),
  )

  // Check if node itself is in an operation
  if (assemblyGroupIdsInOperationStep.includes(node.uuid)) {
    return true
  }

  // Check if any ancestor (parent) is in an operation
  let currentParent = node.parent
  while (currentParent) {
    if (assemblyGroupIdsInOperationStep.includes(currentParent)) {
      return true
    }
    const parentNode = tree.nodes.find((n) => n.uuid === currentParent)
    if (!parentNode) break
    currentParent = parentNode.parent
  }

  // Check if any descendant (child) is in an operation
  const hasDescendantInOperation = (nodeId: string): boolean => {
    const currentNode = tree.nodes.find((n) => n.uuid === nodeId)
    if (!currentNode) return false

    for (const childId of currentNode.children) {
      if (assemblyGroupIdsInOperationStep.includes(childId)) {
        return true
      }
      if (hasDescendantInOperation(childId)) {
        return true
      }
    }
    return false
  }

  if (hasDescendantInOperation(node.uuid)) {
    return true
  }

  return false
}
